import { useContext, useMemo, useEffect, useRef, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useForm, Controller } from 'react-hook-form';
import moment from 'moment';

import {
  faCalendar,
  faTrash,
  faClone,
  faExclamationCircle,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomCalendar from '../../../../../components/calendar/calendar';
import Input from '../../../../../components/input/input';
import Select from '../../../../../components/select/select';
import { PricingContext, GenericPricerParams } from '../../../../../providers/pricing-provider';
import DeleteTradeModal from '../delete-trade-modal';
import './styles.scss';

type Inputs = {
  underlying: string;
  accounting: string;
  contract:
    | 'OneTouch (Upper)'
    | 'OneTouch (Lower)'
    | 'DoubleNoTouch'
    | 'BarrierOption (Upper)'
    | 'BarrierOption (Lower)'
    | 'EuropeanOption'
    | 'AmericanOption';
  expiry: string;
  strike: string;
  callPut: string;
  notionalToken: string;
  notionalAmount: string;
  barrierLower: string;
  barrierUpper: string;
  paymentTime: 'at_touch' | 'at_expiry';
};

const CONTRACT_OPTIONS = [
  { name: 'EuropeanOption', value: 'EuropeanOption' },
  { name: 'AmericanOption', value: 'AmericanOption' },
  { name: 'OneTouch (Upper)', value: 'OneTouch (Upper)' },
  { name: 'OneTouch (Lower)', value: 'OneTouch (Lower)' },
  { name: 'DoubleNoTouch', value: 'DoubleNoTouch' },
  { name: 'BarrierOption (Upper)', value: 'BarrierOption (Upper)' },
  { name: 'BarrierOption (Lower)', value: 'BarrierOption (Lower)' },
];

const PAYMENT_TIME_OPTIONS = [
  { name: 'At touch', value: 'at_touch' },
  { name: 'At expiry', value: 'at_expiry' },
];

const CALL_PUT_OPTIONS = [
  { name: 'Call', value: 'CALL' },
  { name: 'Put', value: 'PUT' },
];

const validateUppercaseLetters = (value: string) => /^[A-Z]*$/.test(value);

const validatePositiveNumber = (value: string) => {
  if (!value) return true;
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

const handleUppercaseChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
) => {
  e.target.value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
  onChange(e);
};

const handleNumericChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
) => {
  const value = e.target.value.replace(/[^0-9.]/g, '');
  const parts = value.split('.');
  e.target.value = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
  onChange(e);
};

interface TradeProps {
  tradeId: string;
}

const Trade = ({ tradeId }: TradeProps) => {
  const {
    fetchGenericPricer,
    registerTradeHandler,
    unregisterTradeHandler,
    deleteTrade,
    duplicateTrade,
    getTradeError,
    getTradeErrorMessage,
    tradeFormData,
    setTradeFormData,
  } = useContext(PricingContext);

  const initialFormData = useMemo(() => {
    const raw = tradeFormData[tradeId] || {};
    return {
      underlying: raw.underlying ?? '',
      accounting: raw.accounting ?? '',
      contract: raw.contract ?? 'OneTouch (Upper)',
      expiry: raw.expiry ?? '',
      strike: raw.strike ?? '',
      callPut: raw.callPut ?? 'CALL',
      notionalToken: raw.notionalToken ?? '',
      notionalAmount: raw.notionalAmount ?? '',
      barrierLower: raw.barrierLower ?? '',
      barrierUpper: raw.barrierUpper ?? '',
      paymentTime: raw.paymentTime ?? 'at_expiry',
    };
  }, [tradeFormData, tradeId]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: initialFormData || {
      underlying: '',
      accounting: '',
      contract: 'OneTouch (Upper)',
      expiry: '',
      strike: '',
      callPut: 'CALL',
      notionalToken: '',
      notionalAmount: '',
      barrierLower: '',
      barrierUpper: '',
      paymentTime: 'at_expiry',
    },
  });

  useEffect(() => {
    const subscription = watch((values) => {
      setTradeFormData((prev) => {
        if (isEqual(prev[tradeId], values)) return prev;
        return {
          ...prev,
          [tradeId]: values,
        };
      });
    });
    return () => subscription.unsubscribe();
  }, [tradeId, setTradeFormData]);

  const [showCalendar, setShowCalendar] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialFormData) {
      Object.entries(initialFormData).forEach(([key, value]) => {
        if (getValues(key as keyof Inputs) !== value) {
          setValue(key as keyof Inputs, value);
        }
      });
    }
  }, [initialFormData, setValue]);

  const submitFormRef = useRef<() => void>(() => {});

  const onSubmit = useCallback(
    (data: Inputs) => {
      const params: GenericPricerParams = {
        tradeId,
        underlying: data.underlying,
        accounting: data.accounting,
        contract: data.contract,
        expiry: data.expiry,
        notionalToken: data.notionalToken,
        notionalAmount: parseFloat(data.notionalAmount),
        barrierUpper: data.barrierUpper ? parseFloat(data.barrierUpper) : undefined,
        barrierLower: data.barrierLower ? parseFloat(data.barrierLower) : undefined,
        paymentTime: data.paymentTime,
        strike: data.strike ? parseFloat(data.strike) : undefined,
        callPut: data.callPut as 'CALL' | 'PUT',
      };
      fetchGenericPricer(params);
    },
    [fetchGenericPricer, tradeId],
  );

  const submitForm = handleSubmit(onSubmit);

  useEffect(() => {
    submitFormRef.current = submitForm;
  }, [submitForm]);

  useEffect(() => {
    registerTradeHandler(tradeId, {
      submit: () => submitFormRef.current(),
      validate: () => trigger(),
      getFormData: () => getValues(),
    });
    return () => unregisterTradeHandler(tradeId);
  }, [tradeId, registerTradeHandler, unregisterTradeHandler, trigger]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        submitFormRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const expiry = watch('expiry');

  const handleDateSelect = (date: Date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    setValue('expiry', formattedDate);
    setShowCalendar(false);
  };

  const handleOpenDeleteTradeModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDuplicateTrade = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const formValues = getValues();
    duplicateTrade(tradeId, formValues);
  };

  const underlying = watch('underlying');
  const accounting = watch('accounting');
  const contract = watch('contract');
  const notionalToken = watch('notionalToken');
  const hasTradeError = getTradeError(tradeId);
  const tradeErrorMessage = getTradeErrorMessage(tradeId);
  const resolvedTradeErrorMessage =
    tradeErrorMessage && tradeErrorMessage !== 'Internal error'
      ? tradeErrorMessage
      : 'An error occurred while calculating your trade. Please try to recalculate.';

  const notionalTokenOptions = useMemo(() => {
    const options = [];
    if (underlying) options.push({ name: underlying, value: underlying });
    if (accounting) options.push({ name: accounting, value: accounting });
    return options;
  }, [underlying, accounting]);

  useEffect(() => {
    if (notionalTokenOptions.length > 0) {
      const currentValue = notionalTokenOptions.find((o) => o.value === notionalToken);
      if (!currentValue) {
        setValue('notionalToken', notionalTokenOptions[0].value);
      }
    }
  }, [notionalTokenOptions, notionalToken, setValue]);
  const isOneTouch = contract === 'OneTouch (Upper)' || contract === 'OneTouch (Lower)';
  const isDoubleNoTouch = contract === 'DoubleNoTouch';
  const isBarrierOption =
    contract === 'BarrierOption (Upper)' || contract === 'BarrierOption (Lower)';

  const isEuropeanOption = contract === 'EuropeanOption';
  const isAmericanOption = contract === 'AmericanOption';
  const isStrikeEnabled = isBarrierOption || isEuropeanOption || isAmericanOption;
  const isCallPutEnabled = isBarrierOption || isEuropeanOption || isAmericanOption;
  const isPaymentTimeEnabled = isOneTouch;

  // Barrier logic
  const isUpperContract = contract === 'OneTouch (Upper)';
  const isBarrierUpperEnabled = isOneTouch
    ? isUpperContract
    : isDoubleNoTouch || contract === 'BarrierOption (Upper)';
  const isBarrierLowerEnabled = isOneTouch
    ? !isUpperContract
    : isDoubleNoTouch || contract === 'BarrierOption (Lower)';

  const isRestrictedNotionalToken = isBarrierOption || isEuropeanOption || isAmericanOption;

  // Notional token logic - BarrierOptions and EuropeanOption must use underlying token
  const effectiveNotionalTokenOptions = useMemo(() => {
    if (isRestrictedNotionalToken && underlying) {
      return [{ name: underlying, value: underlying }];
    }
    return notionalTokenOptions;
  }, [isRestrictedNotionalToken, underlying, notionalTokenOptions]);

  useEffect(() => {
    if (isRestrictedNotionalToken && underlying) {
      setValue('notionalToken', underlying);
    }
  }, [isRestrictedNotionalToken, underlying, setValue]);

  useEffect(() => {
    clearErrors();
    if (!isStrikeEnabled) {
      setValue('strike', '');
    }
    if (!isCallPutEnabled) {
      setValue('callPut', 'CALL');
    }
    if (!isPaymentTimeEnabled) {
      setValue('paymentTime', 'at_expiry');
    }
    if (!isBarrierLowerEnabled) {
      setValue('barrierLower', '');
    }
    if (!isBarrierUpperEnabled) {
      setValue('barrierUpper', '');
    }
  }, [
    contract,
    isStrikeEnabled,
    isCallPutEnabled,
    isPaymentTimeEnabled,
    isBarrierLowerEnabled,
    isBarrierUpperEnabled,
    setValue,
    clearErrors,
  ]);

  return (
    <>
      <DeleteTradeModal
        open={showDeleteModal}
        tradeId={tradeId}
        handleClose={handleCloseDeleteModal}
        handleDelete={deleteTrade}
      />
      <div className="trade">
        <form className={`form ${hasTradeError ? 'has-trade-error' : ''}`} onSubmit={submitForm}>
          <div className="form-row three-columns">
            <Input
              placeholder="Underlying"
              {...register('underlying', {
                required: true,
                validate: validateUppercaseLetters,
              })}
              onChange={(e) => handleUppercaseChange(e, register('underlying').onChange)}
              errors={errors?.underlying ? 'empty' : undefined}
              className="form-input"
            />
            <Input
              placeholder="Accounting"
              {...register('accounting', {
                required: true,
                validate: validateUppercaseLetters,
              })}
              onChange={(e) => handleUppercaseChange(e, register('accounting').onChange)}
              errors={errors?.accounting ? 'empty' : undefined}
              className="form-input"
            />

            <div className="actions-buttons">
              <button
                type="button"
                className="duplicate-trade-button"
                onClick={handleDuplicateTrade}
              >
                <FontAwesomeIcon icon={faClone} />
              </button>
              <button
                type="button"
                className="delete-trade-button"
                onClick={handleOpenDeleteTradeModal}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>

          <div className="form-row">
            <Controller
              name="contract"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  values={CONTRACT_OPTIONS}
                  defaultValue={
                    CONTRACT_OPTIONS.find((o) => o.value === field.value) || CONTRACT_OPTIONS[0]
                  }
                  onChange={field.onChange}
                  className="form-select"
                />
              )}
            />
          </div>

          <div className="form-row">
            <div className="date-picker-wrapper" ref={calendarRef}>
              <div
                className={`date-picker-input ${errors?.expiry ? 'has-error' : ''}`}
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <span className={expiry ? 'date-value' : 'date-placeholder'}>
                  {expiry ? moment(expiry).format('DD/MM/YYYY') : 'dd/mm/yyyy'}
                </span>
                <FontAwesomeIcon icon={faCalendar} className="calendar-icon" />
              </div>
              <input type="hidden" {...register('expiry', { required: true })} />
              {showCalendar && (
                <div className="calendar-dropdown">
                  <CustomCalendar
                    value={expiry || new Date().toISOString()}
                    handler={handleDateSelect}
                    maxDate={new Date('2030-12-31')}
                    minDate={new Date()}
                    newVersion
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <Input
              placeholder="Strike Price"
              {...register('strike', {
                required: isStrikeEnabled,
                validate: validatePositiveNumber,
              })}
              onChange={(e) => handleNumericChange(e, register('strike').onChange)}
              errors={errors?.strike ? 'empty' : undefined}
              disabled={!isStrikeEnabled}
              className="form-input"
            />
          </div>

          <div className="form-row">
            <Controller
              name="callPut"
              control={control}
              rules={{ required: isCallPutEnabled }}
              render={({ field }) => (
                <Select
                  values={CALL_PUT_OPTIONS}
                  defaultValue={
                    CALL_PUT_OPTIONS.find((o) => o.value === field.value) || CALL_PUT_OPTIONS[0]
                  }
                  onChange={field.onChange}
                  disabled={!isCallPutEnabled}
                  className={`form-select ${!isCallPutEnabled ? 'disabled' : ''}`}
                />
              )}
            />
          </div>

          <div className="form-row two-columns">
            <Controller
              name="notionalToken"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  key={effectiveNotionalTokenOptions.map((o) => o.value).join('-')}
                  values={
                    effectiveNotionalTokenOptions.length > 0
                      ? effectiveNotionalTokenOptions
                      : [{ name: '-', value: '' }]
                  }
                  defaultValue={
                    effectiveNotionalTokenOptions.find((o) => o.value === field.value) ||
                    effectiveNotionalTokenOptions[0] || { name: '-', value: '' }
                  }
                  onChange={field.onChange}
                  disabled={isRestrictedNotionalToken}
                  className={`form-select ${isRestrictedNotionalToken ? 'disabled' : ''}`}
                />
              )}
            />
            <Input
              placeholder="Amount"
              {...register('notionalAmount', {
                required: true,
                validate: validatePositiveNumber,
              })}
              onChange={(e) => handleNumericChange(e, register('notionalAmount').onChange)}
              errors={errors?.notionalAmount ? 'empty' : undefined}
              className="form-input"
            />
          </div>

          <div className="form-row two-columns">
            <Input
              placeholder="Lower"
              {...register('barrierLower', {
                required: isBarrierLowerEnabled,
                validate: validatePositiveNumber,
              })}
              onChange={(e) => handleNumericChange(e, register('barrierLower').onChange)}
              errors={errors?.barrierLower ? 'empty' : undefined}
              className="form-input"
              disabled={!isBarrierLowerEnabled}
            />
            <Input
              placeholder="Upper"
              {...register('barrierUpper', {
                required: isBarrierUpperEnabled,
                validate: validatePositiveNumber,
              })}
              onChange={(e) => handleNumericChange(e, register('barrierUpper').onChange)}
              errors={errors?.barrierUpper ? 'empty' : undefined}
              className="form-input"
              disabled={!isBarrierUpperEnabled}
            />
          </div>

          <div className="form-row">
            <Controller
              name="paymentTime"
              control={control}
              rules={{ required: isPaymentTimeEnabled }}
              render={({ field }) => (
                <Select
                  values={PAYMENT_TIME_OPTIONS}
                  defaultValue={
                    PAYMENT_TIME_OPTIONS.find((o) => o.value === field.value) ||
                    PAYMENT_TIME_OPTIONS[0]
                  }
                  onChange={field.onChange}
                  disabled={!isPaymentTimeEnabled}
                  className={`form-select ${!isPaymentTimeEnabled ? 'disabled' : ''}`}
                />
              )}
            />
          </div>
        </form>
        {hasTradeError && (
          <div className="trade-error">
            <FontAwesomeIcon icon={faExclamationCircle} />
            <span>{resolvedTradeErrorMessage}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default Trade;
